/**
 * Shared utility for building memorial display names.
 * Handles both single and double memorials.
 *
 * Usage:
 *   import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
 *   const name = buildMemorialDisplayName(memorial);
 */

interface MemorialNameFields {
  first_name?: string | null;
  last_name?: string | null;
  person2_first_name?: string | null;
  person2_last_name?: string | null;
}

export function buildMemorialDisplayName(
  memorial: MemorialNameFields | null | undefined
): string {
  if (!memorial) return "Memorial";

  const firstName = memorial.first_name?.trim() || "";
  const lastName = memorial.last_name?.trim() || "";
  const person2FirstName = memorial.person2_first_name?.trim() || "";
  const person2LastName = memorial.person2_last_name?.trim() || "";

  if (!firstName && !person2FirstName) return lastName || "Memorial";

  if (person2FirstName) {
    // When person 2 has a different last name, render both full names side by side:
    // "Jane Doe & John Smith". Otherwise fall back to the shared-surname form.
    if (person2LastName && person2LastName !== lastName) {
      const person1Full = lastName ? `${firstName} ${lastName}` : firstName;
      const person2Full = `${person2FirstName} ${person2LastName}`;
      return `${person1Full} & ${person2Full}`;
    }
    // Double memorial, shared surname: "John & Mary Smith"
    const names = `${firstName} & ${person2FirstName}`;
    return lastName ? `${names} ${lastName}` : names;
  }

  // Single memorial: "John Smith"
  return lastName ? `${firstName} ${lastName}` : firstName;
}
